import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import {
  CheckIcon,
  LightbulbIcon,
  PencilIcon,
  PinIcon,
  PlusIcon,
  SettingsIcon,
  UsersIcon,
  XIcon,
} from "lucide-react";

import { hasPermission } from "simplydo/core/permissions";
import { initUseQuery } from "simplydo/core/query";
import { OpenAPI } from "simplydo/interfaces";
import { useTheme } from "simplydo/react/theme";
import { Button, ButtonDivider, ButtonLink } from "simplydo/react/ui/button";
import { Checkbox } from "simplydo/react/ui/checkbox";
import {
  Dropdown,
  DropdownItemLink,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import { Loader } from "simplydo/react/ui/loader";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalDescription,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { EditToolbarItems } from "components/lib/EditToolbar";
import { EntityToolbar, type ToolbarItem } from "components/lib/EntityHeader";
import { ChallengeFollowButton } from "components/lib/FollowButton";

import AdjustableUserList from "./AdjustableUserList";
import ChallengeStageChanger from "./ChallengeStageChanger";

type ChallengeToolbarProps = {
  challengeId: string;
  completed?: {
    audienceCompleted: "done" | "almost" | false;
    templateCompleted: "done" | "almost" | false;
    detailsCompleted: "done" | "almost" | false;
    announcementCompleted: "done" | "almost" | false;
  };
  newIdea: () => void;
  myIdeas: OpenAPI.GET<"/challenges/{id}/ideas">["response"]["ideas"];
  overviewPage: boolean;
  redirectToLogin: (orgCode?: string) => void;
  /** In challenge settings: leads back to the challenge and holds the edit toolbar. */
  settings?: boolean;
};

const ChallengeToolbar = ({
  challengeId,
  completed,
  newIdea,
  myIdeas,
  overviewPage,
  redirectToLogin,
  settings = false,
}: ChallengeToolbarProps) => {
  const [requestContext, setRequestContext] = useState("");
  const user = useCurrentUser();
  const { t } = useTranslation();
  const challengeQuery = useQuery(
    initUseQuery(api.challenges.get, [challengeId]),
  );
  const challenge = challengeQuery.data;

  const [template, setTemplate] = useState({});
  const [requestToOpenChallengeOpen, setRequestToOpenChallengeOpen] =
    useState(false);
  const [selectedRequestAdmin, setSelectedRequestAdmin] = useState("");
  const [requestSent, setRequestSent] = useState(false);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [admins, setAdmins] = useState<
    OpenAPI.GET<"/organisations/{id}/admins">["response"]["users"]
  >([]);
  const [contactingAdmin, setContactingAdmin] = useState(false);
  const [announcements, setAnnouncements] = useState<
    OpenAPI.Schemas["Announcement"][]
  >([]);
  const theme = useTheme();

  const canManageChallenge = useMemo(
    () => util.canManageChallenge(user, challenge),
    [user, challenge],
  );
  const canManageAnnouncements = hasPermission(
    user,
    "challenge.manageAnnouncements",
    challengeId,
  );

  const getChallenge = useCallback(() => {
    if (canManageChallenge || canManageAnnouncements) {
      api.announcements.get(
        "challenge",
        challengeId,
        1,
        (data) => setAnnouncements(data.automaticAnnouncements),
        () => {},
      );
    }
    api.challenges.getTemplate(
      challengeId,
      (newTemplate) => setTemplate(newTemplate),
      () => {},
    );
  }, [challengeId, canManageChallenge, canManageAnnouncements]);

  useEffect(() => getChallenge(), [getChallenge]);

  const userOrg = user?.ownerOrganisation?._id;
  const getAdmins = useCallback(() => {
    if (userOrg) {
      setAdminsLoading(true);
      api.organisations.getAdmins(
        userOrg,
        { limit: 10, permissions: ["org.publishChallenges"] },
        ({ users }) => {
          setAdmins(users);
          setAdminsLoading(false);
        },
        (err) => {
          toast.error(err.message);
          setAdminsLoading(false);
        },
      );
    }
  }, [userOrg]);

  const contactAdmin = useCallback(() => {
    if (!userOrg) return;
    setContactingAdmin(true);
    api.organisations.contactAdmin(
      userOrg,
      {
        challenge: challengeId,
        contactType: "requestOpenChallenge",
        admin: selectedRequestAdmin,
        requestContext,
      },
      () => {
        toast.success("Request sent to admin");
        setSelectedRequestAdmin("");
        setContactingAdmin(false);
        setRequestToOpenChallengeOpen(false);
        setRequestSent(true);
      },
      (err) => {
        toast.error(err.message);
        setContactingAdmin(false);
      },
    );
  }, [selectedRequestAdmin, userOrg, challengeId, requestContext]);

  useEffect(() => {
    if (requestToOpenChallengeOpen) {
      getAdmins();
    }
  }, [requestToOpenChallengeOpen, getAdmins]);

  const updateStage = useCallback(
    (newStage: string, openWithComms = false) => {
      const updateStageFunction = () =>
        api.challenges.updateStage(
          challengeId,
          newStage,
          openWithComms,
          (s) => {
            api.setQueryData(
              api.challenges.get,
              [challengeId],
              (prevChallenge) => ({ ...(prevChallenge || {}), ...s }),
            );
          },
          (err) => {
            toast.error(err.message);
          },
        );

      if (newStage === "closed") {
        util
          .confirm(
            `Really close this ${t("generic.challenge")}?`,
            `Are you sure you want to close this ${t("generic.challenge")}. Closing the ${t("generic.challenge")} will prevent any new ${t("generic.ideas")} or submissions.`,
          )
          .then(() => {
            updateStageFunction();
          })
          .catch(() => {});
      } else updateStageFunction();
    },
    [challengeId, t],
  );

  const pinChallenge = useCallback(() => {
    api.challenges.setStatus(
      challengeId,
      { isPinned: !challenge?.isPinned },
      () => {
        api.setQueryData(
          api.challenges.get,
          [challengeId],
          (prevChallenge) => ({
            ...(prevChallenge || {}),
            isPinned: !challenge?.isPinned,
          }),
        );
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [challengeId, challenge]);

  const canPublishChallenges = useMemo(
    () =>
      util.hasPermission(
        user,
        "org.publishChallenges",
        user?.ownerOrganisation?._id,
      ),
    [user],
  );
  const canMakeIdea = util.canCreateIdea(user, challenge);
  const myUnsubmittedIdeas = useMemo(
    () => myIdeas?.filter((idea) => !idea.isSubmitted),
    [myIdeas],
  );

  const statusLabel = (pointing?: "right") =>
    challenge && theme.sizes.isComputer ? (
      !challenge.stage ? (
        <Label color="yellow" pointing={pointing}>
          {t("challenge.stages.draft.title")}
        </Label>
      ) : challenge.stage === "published" ? (
        <Label color="green" pointing={pointing}>
          {t("challenge.stages.published.title")}
        </Label>
      ) : challenge.stage === "closed" ? (
        <Label color="red" pointing={pointing}>
          {t("generic.closed")}
        </Label>
      ) : null
    ) : null;

  const stageControl =
    challenge &&
    (canManageChallenge ||
      util.hasPermission(user, "challenge.editSettings", challengeId)) ? (
      canPublishChallenges ? (
        <ChallengeStageChanger
          challengeId={challengeId}
          updateStage={updateStage}
          completed={completed}
          announcements={announcements}
          template={template}
          statusLabel={statusLabel("right")}
        />
      ) : challenge.stage !== "published" ? (
        <Button
          size="small"
          disabled={requestSent}
          onClick={() => setRequestToOpenChallengeOpen(true)}
          color="primary"
          labelPosition="left"
          label={statusLabel("right")}
        >
          <Icon icon={requestSent ? CheckIcon : undefined} />
          {t(`challenge.stages.draft.request${requestSent ? "Sent" : ""}`)}
        </Button>
      ) : null
    ) : null;

  const canViewSettings =
    canManageChallenge ||
    util.hasPermission(user, "challenge.viewSettings", challengeId);
  const canEditFormat =
    canManageChallenge ||
    util.hasPermission(user, "challenge.editIdeaFormat", challengeId);
  const canPin = theme.sizes.isMobile
    ? canViewSettings &&
      (canManageChallenge ||
        util.hasPermission(user, "challenge.editSettings", challengeId))
    : !settings &&
      util.hasPermission(
        user,
        "org.manageChallenges",
        user?.ownerOrganisation?._id,
      );

  const ideaButtons =
    challenge &&
    !canViewSettings &&
    !theme.sizes.isMobile &&
    overviewPage &&
    challenge.stage === "published" &&
    !user ? (
      <>
        {(!myUnsubmittedIdeas || myUnsubmittedIdeas?.length === 0) &&
        canMakeIdea ? (
          <Button
            size="small"
            onClick={canMakeIdea ? newIdea : () => redirectToLogin()} // If public idea creation is enabled a user does not need to be logged in
            color="primary"
          >
            <Icon icon={PlusIcon} />
            {myIdeas?.length === 0
              ? challenge.buttonText
                ? challenge.buttonText
                : t("challenge.ideas.createYourIdea")
              : t("challenge.startNew")}
          </Button>
        ) : null}

        {myUnsubmittedIdeas?.length === 1 && (
          <ButtonLink
            size="small"
            color="primary"
            to={`/ideas/${myUnsubmittedIdeas[0]!._id}`}
          >
            <Icon icon={LightbulbIcon} />
            {t("challenge.continueIdea")}
          </ButtonLink>
        )}

        {myUnsubmittedIdeas?.length > 1 && (
          <Dropdown
            trigger={
              <Button size="small" style={{ width: "100%" }} color="primary">
                <Icon icon={LightbulbIcon} />
                {t("challenge.continueIdea")}
              </Button>
            }
          >
            <DropdownMenu>
              {myUnsubmittedIdeas.map((i) => (
                <DropdownItemLink key={i._id} to={`/ideas/${i._id}`}>
                  {i.name}
                </DropdownItemLink>
              ))}
            </DropdownMenu>
          </Dropdown>
        )}
      </>
    ) : null;

  const stageItems: ToolbarItem[] =
    challenge && theme.sizes.isMobile && canPublishChallenges
      ? [
          !challenge.stage
            ? {
                key: "open",
                group: "stage",
                tier: "menu",
                label: t("challenge.stages.draft.open"),
                onClick: () => updateStage("published"),
              }
            : challenge.stage === "published"
              ? {
                  key: "close",
                  group: "stage",
                  tier: "menu",
                  label: t("challenge.stages.published.close"),
                  onClick: () => updateStage("closed"),
                }
              : {
                  key: "reopen",
                  group: "stage",
                  tier: "menu",
                  label: t("challenge.stages.closed.reOpen"),
                  onClick: () => updateStage("published"),
                },
        ]
      : [];

  const items: ToolbarItem[] = challenge
    ? [
        ...(ideaButtons
          ? [
              {
                key: "ideas",
                group: "ideas",
                tier: "pinned" as const,
                label: t("challenge.continueIdea"),
                render: () => ideaButtons,
              },
            ]
          : []),
        ...(settings && theme.sizes.isMobile
          ? [
              {
                key: "overview",
                group: "navigate",
                tier: "default" as const,
                label: t("challenges.viewDetails"),
                to: `/challenges/${challenge._id}`,
              },
            ]
          : []),
        ...(canPin
          ? [
              {
                key: "pin",
                group: "navigate",
                tier: "low" as const,
                label: challenge.isPinned
                  ? t("challenge.ideas.actions.pin.unPin")
                  : t("challenge.ideas.actions.pin.pin"),
                icon: PinIcon,
                variant: "ghost" as const,
                tooltip: t("challenges.pin.info"),
                onClick: pinChallenge,
              },
            ]
          : []),
        ...(!settings && canViewSettings && canEditFormat
          ? [
              {
                key: "format",
                group: "navigate",
                tier: "default" as const,
                label: t("challenge.editFormat"),
                icon: PencilIcon,
                variant: "ghost" as const,
                to: `/challenges/${challenge._id}/settings/format`,
              },
            ]
          : []),
        ...(!settings && canViewSettings
          ? [
              {
                key: "settings",
                group: "navigate",
                tier: "default" as const,
                label: t("challenge.settings.title"),
                icon: SettingsIcon,
                variant: "ghost" as const,
                to: `/challenges/${challenge._id}/settings`,
              },
            ]
          : []),
        ...stageItems,
      ]
    : [];

  return (
    <>
      <Modal
        open={requestToOpenChallengeOpen}
        onClose={() => setRequestToOpenChallengeOpen(false)}
      >
        <ModalHeader>Request to open {t("generic.challenge")}</ModalHeader>
        <ModalContent>
          <ModalDescription>
            <p>
              You do not have permission to open {t("generic.challenges")} in
              your organisation. Please select an admin, who will receive a
              request from you to open this {t("generic.challenge")}.
            </p>
          </ModalDescription>
          {adminsLoading ? (
            <Loader />
          ) : (
            <div>
              <AdjustableUserList
                results={admins}
                actions={(adminUser) => (
                  <Checkbox
                    checked={selectedRequestAdmin === adminUser._id}
                    onClick={() => {
                      setSelectedRequestAdmin((prevId) =>
                        prevId === adminUser._id ? "" : adminUser._id,
                      );
                    }}
                  />
                )}
              />
            </div>
          )}
          <p style={{ marginTop: 10 }}>
            Please add context to your request to help the admin understand why
            you want to open the {t("generic.challenge")}.
          </p>
          <Input
            fluid
            type="text"
            placeholder={`Why do you want to open this ${t("generic.challenge")}?`}
            defaultValue={requestContext}
            onChange={(e, { value }) => setRequestContext(value)}
          />
        </ModalContent>
        <ModalActions>
          <Button onClick={() => setRequestToOpenChallengeOpen(false)}>
            <Icon icon={XIcon} />
            Cancel
          </Button>
          <Button
            disabled={!selectedRequestAdmin}
            loading={contactingAdmin}
            onClick={contactAdmin}
            color="primary"
          >
            <Icon icon={CheckIcon} />
            Send request
          </Button>
        </ModalActions>
      </Modal>
      <EntityToolbar
        start={
          challenge ? (
            <>
              {theme.sizes.isMobile || !stageControl
                ? statusLabel()
                : stageControl}
              {settings && !theme.sizes.isMobile ? (
                <>
                  <ButtonDivider />
                  <ButtonLink
                    size="small"
                    variant="outline"
                    to={`/challenges/${challenge._id}`}
                  >
                    {t("challenges.viewDetails")}
                  </ButtonLink>
                </>
              ) : null}
              {!settings && theme.sizes.isComputer && canViewSettings ? (
                <>
                  <ButtonDivider />
                  <ButtonLink
                    size="small"
                    variant="outline"
                    to={`/challenges/${challenge._id}/ideas`}
                  >
                    <Icon icon={LightbulbIcon} />
                    {t("common:capitalise", { key: "generic.ideas" })}
                    <b style={{ marginLeft: 4, fontWeight: 600 }}>
                      {challenge.ideaCount || 0}
                    </b>
                  </ButtonLink>
                  <ButtonLink
                    size="small"
                    variant="outline"
                    to={`/challenges/${challenge._id}/settings/audience`}
                  >
                    <Icon icon={UsersIcon} />
                    {t("challenge.settings.audience.title")}
                    <b style={{ marginLeft: 4, fontWeight: 600 }}>
                      {challenge.audienceSize || 0}
                    </b>
                  </ButtonLink>
                </>
              ) : null}
              {settings ? null : (
                <ChallengeFollowButton challenge={challenge} />
              )}
            </>
          ) : null
        }
        end={challenge ? <EditToolbarItems items={items} /> : null}
      />
    </>
  );
};

export default ChallengeToolbar;
